import { useState, useRef, useEffect } from 'react';
import { format } from 'date-fns';
import { formatDisplayCalendarDay, formatDisplayTime } from '@/lib/date-format';
import { Send, Settings, Loader2, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type { ProjectChatMessage, CompanyChatMessage, ProjectChatAccess, CompanyChatAccess } from '@/types';

interface ChatMember {
  id: string;
  displayName: string;
  email: string;
  avatarUrl?: string | null;
  canWrite?: boolean;
}

interface ChatViewProps {
  messages: (ProjectChatMessage | CompanyChatMessage)[];
  access: ProjectChatAccess | CompanyChatAccess;
  isLoading?: boolean;
  isSending?: boolean;
  onSendMessage: (content: string) => void;
  onOpenSettings?: () => void;
  title: string;
  emptyMessage?: string;
  currentUserId: string;
  members?: ChatMember[];
  membersLoading?: boolean;
}

export function ChatView({
  messages,
  access,
  isLoading,
  isSending,
  onSendMessage,
  onOpenSettings,
  title,
  emptyMessage = 'No messages yet. Start the conversation!',
  currentUserId,
  members = [],
  membersLoading,
}: ChatViewProps) {
  const [messageInput, setMessageInput] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  const handleSend = () => {
    const content = messageInput.trim();
    if (!content || !access.canWrite || isSending) return;

    onSendMessage(content);
    setMessageInput('');
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, message) => {
    const date = format(new Date(message.createdAt), 'yyyy-MM-dd');
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {} as Record<string, typeof messages>);

  const showSettings = access.canManageSettings && onOpenSettings;

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border flex-shrink-0">
        <h2 className="text-lg font-semibold text-foreground">{title}</h2>
        <div className="flex items-center gap-1">
          {/* Members popover */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-2">
                <Users className="h-4 w-4" />
                <span className="text-sm text-muted-foreground">
                  {membersLoading ? '...' : members.length}
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-0" align="end">
              <div className="p-3 border-b border-border">
                <h3 className="font-medium text-sm">Members ({members.length})</h3>
              </div>
              <ScrollArea className="max-h-64">
                <div className="p-2 space-y-1">
                  {membersLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : members.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No members
                    </p>
                  ) : (
                    members.map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center gap-2 p-2 rounded-md hover:bg-muted/50"
                      >
                        <Avatar className="h-7 w-7">
                          {member.avatarUrl ? (
                            <AvatarImage src={member.avatarUrl} />
                          ) : (
                            <AvatarFallback className="text-xs bg-primary/20 text-primary">
                              {getInitials(member.displayName)}
                            </AvatarFallback>
                          )}
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {member.displayName}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {member.email}
                          </p>
                        </div>
                        {member.canWrite === false && (
                          <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                            Read only
                          </span>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </PopoverContent>
          </Popover>
          {showSettings && (
            <Button variant="ghost" size="icon" onClick={onOpenSettings}>
              <Settings className="h-5 w-5" />
            </Button>
          )}
        </div>
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-muted-foreground">{emptyMessage}</p>
          </div>
        ) : (
          <ScrollArea className="h-full">
            <div ref={scrollRef} className="p-4 space-y-4">
              {Object.entries(groupedMessages).map(([date, dateMessages]) => (
                <div key={date}>
                  {/* Date divider */}
                  <div className="flex items-center gap-2 my-4">
                    <div className="flex-1 border-t border-border" />
                    <span className="text-xs text-muted-foreground px-2">
                      {formatDisplayCalendarDay(date)}
                    </span>
                    <div className="flex-1 border-t border-border" />
                  </div>

                  {/* Messages */}
                  <div className="space-y-3">
                    {dateMessages.map((message, index) => {
                      const isOwn = message.userId === currentUserId;
                      const showAvatar =
                        index === 0 ||
                        dateMessages[index - 1].userId !== message.userId;

                      return (
                        <div
                          key={message.id}
                          className={cn(
                            'flex gap-3',
                            isOwn && 'flex-row-reverse'
                          )}
                        >
                          {showAvatar ? (
                            <Avatar className="h-8 w-8 flex-shrink-0">
                              {message.user.avatarUrl ? (
                                <AvatarImage src={message.user.avatarUrl} />
                              ) : (
                                <AvatarFallback className="text-xs bg-primary text-primary-foreground">
                                  {getInitials(message.user.displayName)}
                                </AvatarFallback>
                              )}
                            </Avatar>
                          ) : (
                            <div className="w-8 flex-shrink-0" />
                          )}

                          <div
                            className={cn(
                              'flex flex-col max-w-[70%]',
                              isOwn && 'items-end'
                            )}
                          >
                            {showAvatar && (
                              <div
                                className={cn(
                                  'flex items-center gap-2 mb-1',
                                  isOwn && 'flex-row-reverse'
                                )}
                              >
                                <span className="text-sm font-medium text-foreground">
                                  {message.user.displayName}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {formatDisplayTime(message.createdAt)}
                                </span>
                              </div>
                            )}
                            <div
                              className={cn(
                                'px-3 py-2 rounded-lg text-sm',
                                isOwn
                                  ? 'bg-primary text-primary-foreground'
                                  : 'bg-muted text-foreground'
                              )}
                            >
                              {message.content}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </div>

      {/* Input area */}
      <div className="p-4 border-t border-border flex-shrink-0">
        {access.canWrite ? (
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message..."
              disabled={isSending}
              className="flex-1"
            />
            <Button
              onClick={handleSend}
              disabled={!messageInput.trim() || isSending}
              size="icon"
            >
              {isSending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        ) : (
          <div className="text-center text-muted-foreground text-sm">
            You don't have permission to write in this chat
          </div>
        )}
      </div>
    </div>
  );
}
